import React, { useEffect, useState, useRef } from "react";
import { string, bool, func, arrayOf, shape } from "prop-types";
import * as Yup from "yup";

import Drawer from "pages/_components/Drawer/Drawer";
import { Field, Form, Formik } from "formik";
import CustomText from "pages/_components/CustomText/CustomText";
import TextField from "pages/_components/fields/TextField";
import PhoneInput from "pages/_components/fields/PhoneInput";
import Button from "pages/_components/Button";
import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { validateEmailWithRegex, validateCUILPF, validatePhone } from "util/form";
import { connect, useDispatch } from "react-redux";
import { selectors as onboardingSelectors } from "reducers/onboarding";
import { selectors as environmentSelectors, actions as environmentActions } from "reducers/environment";
import { actions as notificationActions } from "reducers/notification";
import { usePhoneAreaCodesArg } from "../../../hooks/usePhoneAreaCodesArg";
import Uploader from "./Uploader";

import { useVerifyPersonRoles } from "../hooks/useVerifyPersonRoles";

const FORM_ID = "environment.step.proxies.signer";

const EnvironmentProxySlide = ({
    isMobile,
    slideIsVisible,
    closeSlide,
    onSubmit,
    verifyPersonRequest,
    proxies,
    documentToUploadRequest,
}) => {
    const dispatch = useDispatch();
    const formRef = useRef(null);
    const [submittedValues, setSubmittedValues] = useState({});
    const { argAreaCodes } = usePhoneAreaCodesArg();

    const {
        verifiedUser,
        setVerifyUser,
        disabledForm,
        setDisabledForm,
        updatePhone,
        setUpdatePhone,
        verifyPersonRolesRequest,
        verifyPersonRoles,
        onChangeDocument,
        resetForm,
    } = useVerifyPersonRoles();

    const {
        status: verifyPersonRequestStatus,
        value: verifyPersonRequestValue,
        error: verifyPersonRequestError,
        id: verifyPersonRequestId,
    } = verifyPersonRequest;

    const loadingVerifyPerson =
        (verifyPersonRequestId === submittedValues?.documentNumber && verifyPersonRequestStatus === "FETCHING") ||
        verifyPersonRolesRequest.status === "FETCHING";

    const [thubanCodePoder, setThubanCodePoder] = useState("");

    useEffect(() => {
        const poder = configUtils.get("onboarding.thuban.code.poder", "0009");
        setThubanCodePoder(poder);
    }, []);

    const closeSignerSlide = (allFormik) => {
        setVerifyUser(false);
        setDisabledForm(true);
        allFormik.resetForm();
        closeSlide();
    };

    useEffect(() => {
        const {
            status: verifyPersonRolesRequestStatus,
            value: verifyPersonRolesRequestValue,
        } = verifyPersonRolesRequest;
        if (verifyPersonRolesRequestStatus === "SUCCESS") {
            if (formRef && !formRef.current) {
                return;
            }

            const { invitationData } = verifyPersonRolesRequestValue;

            if (invitationData) {
                formRef.current.setFieldValue("phoneNumber", invitationData?.mobilePhone, false);

                formRef.current.setFieldTouched("phoneNumber", false, false);

                setUpdatePhone(true);

                formRef.current.setFieldValue("email", invitationData?.email);
                formRef.current.setFieldTouched("email", false, false);

                setDisabledForm(true);
            } else {
                resetForm(formRef);
                setDisabledForm(false);
            }
        }
    }, [verifyPersonRolesRequest]);

    useEffect(() => {
        if (verifyPersonRequestId === submittedValues?.documentNumber) {
            if (verifyPersonRequestStatus === "SUCCESS" && verifyPersonRequestValue) {
                const { formikBag, ...onlySubmittedValues } = submittedValues;
                onSubmit({
                    ...onlySubmittedValues,
                    ...verifyPersonRequestValue,
                });
                setSubmittedValues({});
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get("", "Confirmado. Se agregó apoderado."),
                        "success",
                        ["environment"],
                    ),
                );
                dispatch(environmentActions.resetVerifyPerson());
                closeSignerSlide(formikBag);
            } else if (verifyPersonRequestStatus === "FAILURE") {
                dispatch(
                    notificationActions.showNotification(i18nUtils.get("", "Error. apoderado invalido."), "error", [
                        "environment",
                    ]),
                );
                dispatch(environmentActions.resetVerifyPerson());
            }
        }
    }, [verifyPersonRequestStatus, verifyPersonRequestValue, verifyPersonRequestError, verifyPersonRequestId]);

    const validateForm = (val) => {
        if (val.split("").filter((x) => x === "9").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "8").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "7").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "6").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "5").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "4").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "3").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "2").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "1").length > 6) {
            return false;
        }
        if (val.split("").filter((x) => x === "0").length > 6) {
            return false;
        }
        return true;
    };

    const submitIsAllowed = (allFormik) => {
        const { documentNumber, phoneNumber, email, documentProxy } = allFormik.values;

        if (!verifiedUser && !documentNumber) {
            return false;
        }

        if (verifiedUser) {
            if (
                !documentNumber ||
                !phoneNumber ||
                !email ||
                // !((phoneNumber.prefix + phoneNumber.value).length >= 10 && validateForm(phoneNumber.value)) ||
                !documentProxy
            ) {
                return false;
            }
        }

        return true;
    };

    // const requiredString = i18nUtils.get("forms.inputFile.massivePayments.invalid.required");
    const formikProps = {
        enableReinitialize: true,
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            documentNumber: "",
            phoneNumber: { countryCode: "", areaCode: "", value: "" },
            email: "",
            documentProxy: "",
        },
        // eslint-disable-next-line no-confusing-arrow
        validationSchema: () =>
            !verifiedUser
                ? Yup.object().shape({
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                  })
                : Yup.object().shape({
                      documentNumber: Yup.string()
                          .required(i18nUtils.get("global.error.required"))
                          .test(
                              "cuil",
                              i18nUtils.get(
                                  "onboarding.step.personalData.cuil.error.invalidFormat",
                                  "Formato de CUIL inválido",
                              ),
                              (value) => validateCUILPF(value),
                          ),
                      phoneNumber: validatePhone(argAreaCodes),
                      email: Yup.string()
                          .required(i18nUtils.get("onboarding.step.verifyEmail.email.error.empty"))
                          .test(
                              "format",
                              i18nUtils.get("onboarding.step.verifyEmail.email.error.invalidFormat"),
                              (value) => !!value && validateEmailWithRegex(value),
                          ),
                      documentProxy: Yup.string().required(
                          i18nUtils.get("forms.inputFile.massivePayments.invalid.required"),
                      ),
                  }),
        onSubmit: (values, formikBag) => {
            if (values) {
                const found = proxies.find((element) => element.documentNumber === values.documentNumber);

                if (found) {
                    formikBag.setErrors({
                        documentNumber: i18nUtils.get("", "CUIL ya se encuentra agregado"),
                    });
                    return;
                }

                if (verifiedUser) {
                    const finalValues = {
                        formikBag,
                        documentNumber: values.documentNumber,
                        documentType: "CUIL",
                        phoneNumber: values.phoneNumber,
                        email: values.email,
                        documentProxy: values.documentProxy,
                    };

                    setSubmittedValues(finalValues);
                    dispatch(
                        environmentActions.verifyPerson({
                            documentNumber: finalValues?.documentNumber,
                            documentType: "CUIL",
                            personType: "NATURAL_PERSON",
                        }),
                    );
                } else {
                    verifyPersonRoles(values?.documentNumber, "FIRMANTE_APODERADO", true);
                }
            }
        },
    };

    return (
        <Formik {...formikProps} ref={formRef}>
            {(allFormik) => (
                <Drawer
                    className="signer-slider"
                    openSidebar={slideIsVisible}
                    notCreateInRoot
                    toggleSidebar={() => closeSignerSlide(allFormik)}>
                    <div className="container">
                        <h2 className="title-content">
                            <CustomText text={i18nUtils.get("", "Apoderado")} />
                        </h2>
                        <Form>
                            <div>
                                <Field
                                    autoComplete="off"
                                    component={TextField}
                                    idForm={FORM_ID}
                                    maxLength={50}
                                    name="documentNumber"
                                    labelTextDefault={i18nUtils.get("", "CUIL")}
                                    hidePlaceholder
                                    autoFocus={!isMobile}
                                    handleOnChange={(ev) => onChangeDocument(ev)}
                                />
                                <Field
                                    component={PhoneInput}
                                    idForm={FORM_ID}
                                    name="phoneNumber"
                                    country="AR"
                                    mobilePhone="54"
                                    enableSearch={false}
                                    preferredCountries={["ar"]}
                                    labelTextDefault={i18nUtils.get("", "Celular")}
                                    hintLabel={i18nUtils.get("", "Debes incluir el código de área y luego el número.")}
                                    disabled={disabledForm}
                                    updatePhone={updatePhone}
                                    phoneAlreadyUpdated={() => setUpdatePhone(false)}
                                />
                                <Field
                                    autoComplete="off"
                                    component={TextField}
                                    idForm={FORM_ID}
                                    name="email"
                                    labelTextDefault={i18nUtils.get("", "Email")}
                                    spaceEnabled={false}
                                    hidePlaceholder
                                    disabled={disabledForm}
                                />
                                <Field
                                    component={Uploader}
                                    idForm={FORM_ID}
                                    name="documentProxy"
                                    label="Adjuntá documentación de poder"
                                    documentCode={thubanCodePoder}
                                    allowedTypes={["pdf"]}
                                    extensionDocument="pdf"
                                    documentNumber={allFormik.values.documentNumber}
                                    isValid={slideIsVisible}
                                />
                            </div>
                            <div className="grid-table form-actions">
                                <div className="grid-table-row">
                                    <div className="grid-table-data">
                                        <Button
                                            bsStyle="primary"
                                            label={verifiedUser ? "global.add" : "onboarding.cuil.search.button"}
                                            type="submit"
                                            block
                                            disabled={
                                                !submitIsAllowed(allFormik) ||
                                                documentToUploadRequest.status === "FETCHING"
                                            }
                                            loading={loadingVerifyPerson}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Form>
                    </div>
                </Drawer>
            )}
        </Formik>
    );
};

EnvironmentProxySlide.propTypes = {
    isMobile: bool.isRequired,
    page: string.isRequired,
    slideIsVisible: bool.isRequired,
    closeSlide: func.isRequired,
    onSubmit: func.isRequired,

    pepJobsList: arrayOf(shape({})).isRequired,
    pepRelationshipTypesList: arrayOf(shape({})).isRequired,
    countryList: arrayOf(shape({})).isRequired,

    nosisCdaRequest: arrayOf(shape({})).isRequired,
    verifyPersonRequest: arrayOf(shape({})).isRequired,
    proxies: arrayOf(shape({})).isRequired,
    documentToUploadRequest: shape({}).isRequired,
};

const mapStateToProps = (state) => ({
    pepJobsList: onboardingSelectors.getPepJobsList(state),
    pepRelationshipTypesList: onboardingSelectors.getPepRelationshipTypesList(state),
    countryList: onboardingSelectors.getCountryList(state),

    nosisCdaRequest: onboardingSelectors.getNosisCdaRequest(state),
    verifyPersonRequest: environmentSelectors.getVerifyPersonRequest(state),
    documentToUploadRequest: environmentSelectors.getDocumentToUploadRequest(state),
});

export default connect(mapStateToProps)(EnvironmentProxySlide);
